import { computed, inject, Injectable } from '@angular/core';
import { signal } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CachedData, OrderApiResponseData } from '../../../../../models/order/api-models/order-api-response.model';
import { OrderApiCacheService } from './order-api-cache.service';
import { ProductType } from '../../../../../models/order/order.model';
import { EncryptDecryptService } from '../../../../../services/encrypt-decrypt.service';
import { EnvironmentService } from '../../../../../services/environment.service';
import { VideoPackageTypeEnum } from '../../../../../enums/video-package-type.enum';

@Injectable({ providedIn: 'root' })
export class OrderApiResponseStateService {
    private orderService = inject(OrderApiCacheService);
    encryptDecryptService: EncryptDecryptService = inject(EncryptDecryptService)
    environmentService: EnvironmentService = inject(EnvironmentService)

    // signal holds All API responses
    private _apiData = signal<OrderApiResponseData | null>(null);

    /** Raw combined API responses */
    apiData = this._apiData.asReadonly();

    /** Order Number */
    orderNumber = computed(() => this._apiData()?.OrderNumber ?? null);

    orderData = computed(() => this._apiData()?.CachedData?.ORDER_DATA ?? null);
    hasOrderData = computed(() => !!this.orderData());

    // PreOrder section
    preOrderData = computed(() => this._apiData()?.CachedData?.PREORDER ?? null);
    hasPreOrderData = computed(() => !!this.preOrderData());

    // StartOrder section
    startOrderData = computed(() => this._apiData()?.CachedData?.STARTORDER ?? null);
    hasStartOrderData = computed(() => !!this.startOrderData());

    // Service Availability/SD section
    serviceAvailabilityData = computed(() => this.apiData()?.CachedData?.SQ ?? null);
    customer = computed(() => {
        const updateCustomer = this.apiData()?.CachedData?.UPDATE_CUSTOMER;
        const sqCustomer = this.apiData()?.CachedData?.SQ?.Customer;
        const base = updateCustomer ?? sqCustomer;
        if (!base) {
            return null;
        }
        return { ...base, ServiceAddress: sqCustomer?.ServiceAddress ?? base.ServiceAddress };
    });
    serviceAvailabilityProductResponse = computed(() => this.serviceAvailabilityData()?.Response?.ProductResponse ?? []);

    // Product Details section
    internetOrderRequirements = computed(() => this.internetProductDetailsData()?.OrderRequirements?.Internet ?? null);

    videoOrderRequirements = computed(() => this.videoProductDetailsData()?.OrderRequirements?.Video ?? null);

    wirelessOrderRequirements = computed(() => this.wirelessProductDetailsData()?.OrderRequirements?.Wireless ?? null);
    // Video Details section
    videoProductDetailsData = computed(() => {
        return this.apiData()?.CachedData?.VIDEO_PD ?? null;
    });
    // Internet Product Details section
    internetProductDetailsData = computed(() => this.apiData()?.CachedData?.INTERNET_PD ?? null);

    // Voice Product Details section
    voiceProductDetailsData = computed(() => this.apiData()?.CachedData?.VOICE_PD ?? null);

    // Wireless Product Details section
    wirelessProductDetailsData = computed(() => this.apiData()?.CachedData?.WIRELESS_PD ?? null);

    wirelessProductDetailsPlusData = computed(() => this.apiData()?.CachedData?.WIRELESS_PDPLUS ?? null);

    // CC section
    creditCheckData = computed(() => this.apiData()?.CachedData?.CCPLUS ?? (this.apiData()?.CachedData?.CC ?? null));

    //Internet Update Offer data
    internetUpdateOfferData = computed(() => this.apiData()?.CachedData?.INTERNET_UO ?? null);

    //Video Update Offer data
    videoUpdateOfferData = computed(() => this.apiData()?.CachedData?.VIDEO_UO ?? null);

    // Credit Check Details from CO section
    createOrderData = computed(() => this.apiData()?.CachedData?.CO ?? null);

    // Order Qualification Details from OQ section
    orderQualificationData = computed(() => this.apiData()?.CachedData?.OQ ?? null);

    // ATT section
    attData = computed(() => this.apiData()?.CachedData?.ATT ?? null);

    // RC1 section
    rc1Data = computed(() => this.apiData()?.CachedData?.RC1 ?? null);

    // VI_Payment section
    viPaymentData = computed(() => this.apiData()?.CachedData?.VI_PAYMENT ?? null);

    // VI_Payment section
    dtvPaymentData = computed(() => this.apiData()?.CachedData?.DTV_PAYMENT ?? this.apiData()?.CachedData?.DTVCOMM_PAYMENT ?? null);

    // VI_AuthGetAccount section
    viAuthGetAccountData = computed(() => this.apiData()?.CachedData?.VI_AUTH_GET_ACCOUNT ?? null);
    viAuthGetWirelessAccountData = computed(() => this.viAuthGetAccountData()?.ResponseHeader?.ProductResponse?.find(x => x.Type.toLowerCase() === ProductType.Wireless.toLowerCase()) ?? null);

    // SO section
    submitOrderData = computed(() => this.apiData()?.CachedData?.SO ?? null);

    // OQ section

    /** Load entire API data */
    loadOrderData(orderId: string): Observable<OrderApiResponseData> {
        return this.orderService.getOrderApiCache(orderId).pipe(
            tap(response => {
                if (response?.CachedData?.ORDER_DATA && typeof response.CachedData.ORDER_DATA === 'string') {
                    response.CachedData.ORDER_DATA = JSON.parse(response.CachedData.ORDER_DATA);
                }
                this._apiData.set(response)
            })
        );
    }

    storeUpdateOffer(updateOffers: any) {
        const type = updateOffers?.Components?.Component?.ProductType?.toUpperCase();
        if (type === VideoPackageTypeEnum.ATV || type === VideoPackageTypeEnum.DTV) {
            this.updateCachedData('VIDEO_UO', updateOffers);
        } else if (type === 'INTERNET' || type === 'OOF') {
            this.updateCachedData('INTERNET_UO', updateOffers);
        }
    }

    updateCachedData(sectionName: (keyof CachedData), data: any) {
        let currentData = this._apiData();
        if (!currentData) {
            currentData = {
                CachedData: null,
                OrderNumber: ''
            }
        }
        const updatedCachedData = {
            ...currentData.CachedData,
            [sectionName]: data
        };
        this._apiData.set({
            ...currentData,
            CachedData: updatedCachedData
        });
    }

    /** Clear stored API data */
    clear() {
        this._apiData.set(null);
    }

    /** Helper to get any section dynamically */
    getSection<T = any>(sectionName: string): T | null {
        const data = this._apiData();
        return data && data[sectionName] ? data[sectionName] as T : null;
    }
}